import React, { useEffect, useState, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { QuestionRendererProps } from './types';
const DEFAULT_COLORS = ['#6366f1', '#14b8a6', '#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f43f5e' // rose-500
];
export function PieChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  // Read from question.data (canonical), fallback to interactiveConfig for legacy
  const config = question.data || question.interactiveConfig || {};
  // Transform flat arrays into Recharts format
  const labels = config.labels || [];
  const values = config.values || [];
  const title = config.title || '';
  const colors = config.colors || DEFAULT_COLORS;
  const hideValues = config.hideValues !== false; // Default: hide values in tooltip
  const chartData = labels.map((label: string, index: number) => ({
    name: label,
    value: values[index] || 0,
    originalIndex: index
  }));
  const prevValidateRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Reset state on question change
  useEffect(() => {
    setSelectedIndex(null);
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  // Notify parent when ready state changes
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedIndex !== null) {
      prevValidateRef.current = true;
      onAnswer(String(selectedIndex));
    }
  }, [shouldValidate, selectedIndex, onAnswer]);
  const handlePieClick = (_data: any, index: number) => {
    setSelectedIndex(index === selectedIndex ? null : index);
  };
  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-4601">
        <span className="font-semibold text-slate-700" data-id="element-4602">{item.name}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-4603">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-4604">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col" data-id="element-4605">
        {title && <h3 className="text-center text-slate-700 font-semibold mb-2" data-id="element-4606">
            {title}
          </h3>}
        <div className="flex-1 w-full relative" data-id="element-4607">
          <ResponsiveContainer width="100%" height="100%" data-id="element-4608">
            <PieChart data-id="element-4609">
              <Pie data={chartData} cx="50%" cy="50%" innerRadius="40%" outerRadius="80%" paddingAngle={2} dataKey="value" onClick={handlePieClick} cursor="pointer" data-id="element-4610">
                {chartData.map((entry: any, index: number) => {
                const isSelected = selectedIndex === index;
                return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke={isSelected ? '#1e1b4b' : '#ffffff'} strokeWidth={isSelected ? 3 : 1} className="transition-all duration-200" style={{
                  opacity: selectedIndex !== null && !isSelected ? 0.6 : 1,
                  filter: isSelected ? 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))' : 'none'
                }} data-id="element-4611" />;
              })}
              </Pie>
              <Tooltip content={<CustomTooltip data-id="element-4613" />} data-id="element-4612" />
              <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={value => <span className="text-slate-600 text-sm" data-id="element-4615">{value}</span>} data-id="element-4614" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-4616">
        Klik op het juiste deel van het cirkeldiagram
      </p>
    </div>;
}